import { createStore } from 'vuex'
import indoorviewer from './modules/indoorviewer'
import mapview from './modules/mapview'
import planview from './modules/planview'
import dashboardview from './modules/dashboardview'
import {
  ADD_MAP_LAYER,
  REMOVE_MAP_LAYER,
  RESET_CURRENT_BUILDING,
  RESET_CURRENT_COMPLEX_LAYER,
  RESET_CURRENT_ROOM,
  RESET_CURRENT_TOOL,
  SET_BUILDING_SOURCE,
  SET_CLICKED_ITEM_TYPE,
  SET_COMPLEX_SOURCE,
  SET_CURRENT_BUILDING,
  SET_CURRENT_COMPLEX_LAYER,
  SET_CURRENT_COUNTY,
  SET_CURRENT_DATACOMPARISON,
  SET_CURRENT_DRONEVIEW_LINK_ID,
  SET_CURRENT_LEVEL,
  SET_CURRENT_OVERLAY_MODAL,
  SET_CURRENT_PANORAMA_URL,
  SET_CURRENT_PITCH,
  SET_CURRENT_PREVIEW_FILE,
  SET_CURRENT_REAL_ESTATE,
  SET_CURRENT_ROOM,
  SET_CURRENT_TOOL,
  SET_CURRENT_VIEW,
  SET_CURRENT_ZOOM,
  SET_DATACOMPARISON_INTERVAL,
  SET_DRAW_POINT_FEATURE,
  SET_FILTERED_MAP_LAYERS,
  SET_LEVELS_FOR_SELECTED_BUILDING,
  SET_MAP_IS_LOADED,
  SET_MENU_EXPANDED,
  SET_MOBILE_SCREEN,
  SET_PREVIOUS_VIEW,
  SET_REAL_ESTATE_SOURCE,
  SET_REALM_ID,
  SET_ROOMS_FOR_SELECTED_LEVEL,
  SET_SHOW_ZONE_MENU,
  SET_STATUS_ON_SHOW_OVERLAY_MODAL,
  SET_ZONE_CATEGORIES_FOR_REAL_ESTATE,
  SET_ZONE_CATEGORIES_FOR_SELECTED_LEVEL
} from './mutation-types'
import {
  CHANGE_BUILDING,
  CHANGE_COMPLEX,
  CHANGE_LEVEL,
  CHANGE_REAL_ESTATE,
  CHANGE_ROOM,
  GET_ROOMS_FOR_SELECTED_LEVEL,
  RESET_ALL_SELECTIONS,
  RESET_CURRENT_LEVEL,
  RESET_CURRENT_REAL_ESTATE,
  RESET_PLANVIEW_SELECTIONS,
  IMPORT_STATE
} from './action-types'
import { getLevelv1 } from '../services/floors.service'
import { getRoomsForSelectedLevelv1, getSpacev1 } from '@/services/rooms.service'
import { HTTPGatewayClient } from '@/services/http-client'
import { calculateLevelSource, formatNavvisLink } from './helpers'
import { orderByOrder } from '@/utils/sort'
import { useGeneralStore as piniaStore } from '@/pinia'
import {
  convertFloorPlanToDrawing,
  convertOutdoorImageToOrthophoto,
  convertOutdoorPlanToDrawing,
  convertEntityToDrawing
} from '@/utils/drawing'
import { useEntityService } from '@/services/entity.service'

export default createStore({
  state: {
    buildingSource: null,
    clickedItemType: 'region', // change to overview as default when we make separation for overview from region
    complexSource: null,
    currentDataComparison: 'BTA',
    currentDroneviewLinkId: null,
    currentObject: {},
    currentOverlayModal: '',
    currentPitch: 0,
    currentPreviewFile: null,
    currentTool: null,
    currentView: 'mapview',
    currentZoom: 4,
    dataComparisonInterval: { low: 3000, high: 7000 },
    drawPointFeature: null,
    infoMessage: null,
    levelsForSelectedBuilding: null,
    mapIsLoaded: false,
    menuExpanded: false,
    mobileScreen: false,
    mountedMapLayerIds: [],
    previousView: 'mapview',
    realEstateSource: null,
    realmId: null,
    roomsForSelectedLevel: null,
    selectedLevel: null,
    selectedRoom: null,
    showOverlayModal: false,
    showZoneMenu: false,
    toolboxMenuActive: false,
    activeControllers: []
  },
  getters: {
    allComplexes: (state) => {
      return state.complexSource?.data?.features ?? []
    },
    allRealEstates: (state) => {
      return state.realEstateSource?.data?.features ?? []
    },
    allBuildings: (state) => {
      return state.buildingSource?.data?.features ?? []
    },
    isDashboardviewActiveFromMapview: (state) => {
      return state.currentView === 'dashboard' && state.previousView === 'mapview'
    },
    isDashboardviewActiveFromPlanview: (state) => {
      return state.currentView === 'dashboard' && state.previousView === 'planview'
    },
    isMeasurementActive: (state) => {
      return state.currentTool == 'areameasurement' || state.currentTool == 'linemeasurement'
    },
    isDataComparisonToolActive: (state) => {
      return state.currentTool == 'datacomparisontool'
    },
    isDrawPointActive: (state) => {
      return state.currentTool == 'drawpoint'
    },
    isPlanviewShowing: (state) => {
      return state.currentView === 'planview'
    },
    isMapviewShowing: (state) => {
      return state.currentView === 'mapview'
    },
    isModelviewShowing: (state) => {
      return state.currentView === 'modelview'
    },
    isIndoorviewShowing: (state) => {
      return state.currentView === 'indoorview'
    },
    isDroneviewShowing: (state) => {
      return state.currentView === 'droneview'
    },
    isDashboardviewShowing: (state) => {
      return state.currentView === 'dashboard'
    },
    realmLogo: (state) => {
      function getImageUrl(name, ext) {
        return new URL(`/src/assets/logo/${name}.${ext}`, import.meta.url).href
      }
      switch (state.realmId) {
        case 'akademiskahus':
          return getImageUrl(state.realmId, 'svg')
        case 'ica':
          return getImageUrl(state.realmId, 'svg')
        case 'zdb':
          return getImageUrl(state.realmId, 'svg')
        case 'dbproperties':
          return getImageUrl(state.realmId, 'svg')
        case 'lc-and-partners':
          return getImageUrl('logo_lc_p', 'png')
        case 'sam-dev':
          return getImageUrl('sam-logo', 'png')
        case 'sam':
          return getImageUrl('sam-logo', 'png')
        default:
          return ''
      }
    },
    selectedLevelId: (state) => {
      if (state.selectedLevel == null) return null
      return state.selectedLevel.id
    },
    selectedLevelNumber: (state) => {
      if (state.selectedLevel == null) return null
      return state.selectedLevel.level
    },
    selectedRoomId: (state) => {
      if (state.selectedRoom == null) return null
      return state.selectedRoom.properties.id
    },
    selectedRoomPosition: (state) => {
      return state.selectedRoom?.properties?.location?.coordinates
    },
    // the different states needs to be made and added if they dont exist.
    selectedObject: (state) => {
      switch (state.clickedItemType) {
        case 'region':
          return state.mapview.selectedRegion
        case 'complex':
          return state.mapview.selectedComplex
        case 'realEstate':
          return state.mapview.selectedRealEstate
        case 'building':
          return state.mapview.selectedBuilding
        case 'level':
          return state.selectedLevel
        case 'room':
          return state.selectedRoom?.properties
        case 'line':
          return piniaStore().selectedLine
        case 'zone':
          return piniaStore().selectedZone
      }
    },
    selectedObjectId: (state, getters) => {
      if (!getters.selectedObject) return ''
      let selectedObjectId
      if (getters.selectedObject === 0) {
        // 0 is for overview (REGION), use realmId
        selectedObjectId = state.realmId ?? ''
      } else if (state.clickedItemType === 'zone') {
        selectedObjectId = piniaStore().selectedZone?.layerId //use layerID for zone (handles files and file placement)
      } else if (state.clickedItemType === 'line') {
        selectedObjectId = piniaStore().selectedLine?.layerId //use layerID for line (handles files and file placement)
      } else {
        selectedObjectId = getters.selectedObject.guid ?? getters.selectedObject.id // Use guid if available
        if (!selectedObjectId) selectedObjectId = getters.selectedObject.belongsToId // Necessary for complex
      }
      return selectedObjectId
    }
  },
  mutations: {
    setActiveControllers(state, controllers) {
      state.activeControllers = controllers
    },
    clearActiveControllers(state) {
      state.activeControllers.forEach((c) => c.abort())
      state.activeControllers = []
    },
    [SET_COMPLEX_SOURCE](state, payload) {
      state.complexSource = payload
    },
    [SET_REAL_ESTATE_SOURCE](state, payload) {
      state.realEstateSource = payload
    },
    [SET_BUILDING_SOURCE](state, payload) {
      state.buildingSource = payload
    },
    [SET_CURRENT_DRONEVIEW_LINK_ID](state, payload) {
      state.currentDroneviewLinkId = payload
    },
    [SET_CURRENT_PITCH](state, payload) {
      state.currentPitch = payload
    },
    [SET_CURRENT_ZOOM](state, payload) {
      state.currentZoom = payload
    },
    [SET_MAP_IS_LOADED](state) {
      state.mapIsLoaded = true
    },
    [RESET_CURRENT_ROOM](state) {
      state.selectedRoom = null
    },
    [RESET_CURRENT_TOOL](state) {
      state.currentTool = null
    },
    [SET_CLICKED_ITEM_TYPE](state, payload) {
      if (
        ![
          'room',
          'level',
          'county',
          'building',
          'region',
          'complex',
          'realEstate',
          'line',
          'zone'
        ].includes(payload.type)
      ) {
        console.error('Item type ' + payload.type + ' is not supported')
      } else {
        state.clickedItemType = payload.type
      }
    },
    // Takes a payload with type: String
    [SET_CURRENT_COUNTY](state, payload) {
      state.clickedCounty = payload.county
    },
    // Takes a payload with county: Object
    [SET_CURRENT_DATACOMPARISON](state, payload) {
      state.currentDataComparison = payload.currentDataComparison
    },
    [SET_DRAW_POINT_FEATURE](state, payload) {
      state.drawPointFeature = { ...payload.point }
    },
    [SET_CURRENT_LEVEL](state, payload) {
      state.selectedLevel = payload.properties
    },
    [SET_CURRENT_OVERLAY_MODAL](state, payload) {
      state.currentOverlayModal = payload.modalName
    },
    // Takes a payload with the name of active modal: String
    [SET_CURRENT_PREVIEW_FILE](state, payload) {
      state.currentPreviewFile = { url: payload.url, type: payload.type }
    },
    [SET_CURRENT_PANORAMA_URL](state, payload) {
      state.pannoramaUrl = payload
    },
    [SET_SHOW_ZONE_MENU](state, payload) {
      state.showZoneMenu = payload
    },
    [SET_CURRENT_ROOM](state, payload) {
      state.selectedRoom = payload
    },
    // Takes a payload with building: Object
    [SET_CURRENT_TOOL](state, payload) {
      if (
        !['areameasurement', 'linemeasurement', 'datacomparisontool', 'drawpoint'].includes(
          payload.tool
        )
      ) {
        console.error(
          'Current tool must be of type areameasurement, linemeasurement, datacomparisontool or drawpoint'
        )
      } else {
        state.currentTool = payload.tool
      }
    },
    [SET_CURRENT_VIEW](state, payload) {
      if (
        !['mapview', 'planview', 'modelview', 'indoorview', 'droneview', 'dashboard'].includes(
          payload.view
        )
      ) {
        console.error(
          'Current view must be of type mapview, planview, modelview, dashboard, droneview or indoorview'
        )
      } else {
        state.currentView = payload.view
      }
    },
    // Takes a payload with view: String
    [SET_DATACOMPARISON_INTERVAL](state, payload) {
      const newInterval = { ...state.dataComparisonInterval }
      newInterval.low = payload.comparisonInterval.low
      newInterval.high = payload.comparisonInterval.high
      state.dataComparisonInterval = newInterval
    },
    [SET_LEVELS_FOR_SELECTED_BUILDING](state, payload) {
      state.levelsForSelectedBuilding = payload.levels
    },
    [SET_MOBILE_SCREEN](state, payload) {
      state.mobileScreen = payload.mobileScreen
    },
    [SET_MENU_EXPANDED](state, payload) {
      state.menuExpanded = payload.menuExpanded
    },
    [SET_ROOMS_FOR_SELECTED_LEVEL](state, payload) {
      state.roomsForSelectedLevel = payload.rooms
    },
    // Takes a payload with active or not (toggle between show/not show modal): Boolean
    [SET_STATUS_ON_SHOW_OVERLAY_MODAL](state, payload) {
      state.showOverlayModal = payload.toggleModal
    },
    [SET_PREVIOUS_VIEW](state, payload) {
      state.previousView = payload.view
    },
    [SET_REALM_ID](state, payload) {
      state.realmId = payload.realmId
    },
    [ADD_MAP_LAYER](state, payload) {
      if (state.mountedMapLayerIds.includes(payload)) return
      state.mountedMapLayerIds.push(payload)
    },
    [REMOVE_MAP_LAYER](state, payload) {
      state.mountedMapLayerIds = state.mountedMapLayerIds.filter((id) => id !== payload)
    },
    setInfoMessage(state, payload) {
      state.infoMessage = payload
    },

    setToolboxMenuActive(state, payload) {
      state.toolboxMenuActive = payload
    }
  },
  actions: {
    [CHANGE_BUILDING]({ commit, dispatch, state, getters }, payload) {
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      commit(RESET_CURRENT_ROOM)
      dispatch('RESET_CURRENT_LEVEL')
      commit('mapview/' + RESET_CURRENT_BUILDING)

      state.activeControllers.forEach((controller) => controller.abort())
      if (!payload.building || payload.building == {}) return

      // useSelectionStore().set([payload.building.id])

      const complexId = payload.building.properties.complexId
      if (complexId && complexId !== 'undefined') dispatch('setSelectedComplexById', complexId)
      else commit('mapview/' + RESET_CURRENT_COMPLEX_LAYER)

      const realEstateId = payload.building.properties.realEstateId

      if (!complexId || complexId === 'undefined') {
        const realEstate = getters.allRealEstates.find(
          (realEstate) => realEstate.properties.id === realEstateId
        )

        if (realEstate) {
          dispatch('setSelectedComplexById', realEstate.properties.complexId)
        }
      }

      if (realEstateId && realEstateId !== 'undefined')
        dispatch('setSelectedRealEstateById', realEstateId)
      else dispatch('mapview/' + RESET_CURRENT_REAL_ESTATE)

      const feature = { ...payload.building }
      const controllers = []

      function fetchBuildingProperties(id) {
        const controller = new AbortController()
        const endpoint = `core/v1/constructions/${id}`
        controllers.push(controller)
        return HTTPGatewayClient.get(endpoint, { signal: controller.signal })
      }

      function fetchBuildingLevelsProperties(id) {
        const controller = new AbortController()
        const endpoint = `core/v1/levels?constructionId=${id}`
        controllers.push(controller)
        return HTTPGatewayClient.get(endpoint, { signal: controller.signal })
      }

      function fetchBuildingLevelsFeatures(id) {
        const controller = new AbortController()
        const endpoint = `core/v1/levels/geojson?constructionId=${id}`
        controllers.push(controller)
        return HTTPGatewayClient.get(endpoint, { signal: controller.signal })
      }

      const promises = [
        fetchBuildingProperties(feature.id),
        fetchBuildingLevelsProperties(feature.id),
        fetchBuildingLevelsFeatures(feature.id)
      ]

      commit('setActiveControllers', controllers)

      return Promise.all(promises.map((p) => p.catch((error) => error)))
        .then((responses) => {
          updateFeature(responses[0])
          updateLevels(responses[1], responses[2])
        })
        .catch((error) => console.debug('error', error))
        .finally(() => commit('clearActiveControllers'))

      function updateFeature(buildingDataResponse) {
        const buildingData = { ...buildingDataResponse.data }
        const measurements = buildingData?.measurements?.[0] || {}

        feature.properties.geometry = feature.geometry
        feature.properties.measurements = measurements
        feature.properties.navvisLinks = feature.properties.navvisLinks
          .map(formatNavvisLink)
          .sort(orderByOrder)
        commit('mapview/' + SET_CURRENT_BUILDING, {
          building: feature.properties
        })
      }

      function updateLevels(levelsResponse, levelsFeaturesResponse) {
        const levelSource = calculateLevelSource(
          payload.building.geometry,
          levelsFeaturesResponse.data,
          levelsResponse.data
        )
        commit(SET_LEVELS_FOR_SELECTED_BUILDING, { levels: levelSource })
      }
    },
    // Takes a payload complex: String, the externalId of current complex (such as got1)
    [CHANGE_COMPLEX]({ commit, dispatch }, payload) {
      // useSelectionStore().set([payload.complex.belongsToId])
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      commit(RESET_CURRENT_ROOM)
      dispatch('RESET_CURRENT_LEVEL')
      commit('mapview/' + RESET_CURRENT_BUILDING)
      dispatch('mapview/' + RESET_CURRENT_REAL_ESTATE)
      if (payload.complex === '') {
        commit('mapview/' + RESET_CURRENT_COMPLEX_LAYER)
      } else {
        commit('mapview/' + SET_CURRENT_COMPLEX_LAYER, payload)
      }
    },
    async [CHANGE_LEVEL]({ commit, state, dispatch }, payload) {
      const { levelNumber } = payload
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      commit(RESET_CURRENT_ROOM)
      commit(SET_ROOMS_FOR_SELECTED_LEVEL, { rooms: null })
      commit(SET_SHOW_ZONE_MENU, false)
      commit('planview/' + SET_ZONE_CATEGORIES_FOR_SELECTED_LEVEL, { categories: [] })
      piniaStore().setFilteredDrawings([])
      piniaStore().setFilteredOrthophotos([])

      const levelFeatures = state.levelsForSelectedBuilding?.data?.features
      if (!levelFeatures || levelFeatures.length === 0) return

      const level = levelFeatures.find((level) => level.properties.level === levelNumber)
      if (!level) return

      // useSelectionStore().set([level.id])

      const levelPropsResult = await getLevelv1(level.id)
      const levelProps = levelPropsResult.data

      level.properties.models3d = levelProps.models3d ?? []

      let filteredDrawings = []
      if (level.properties.hasFloorPlans?.length) {
        filteredDrawings = level.properties.hasFloorPlans
          .filter((floorPlan) => floorPlan.type !== 'SCAN')
          .map((floorPlan) => convertFloorPlanToDrawing(floorPlan))
      }

      let filteredOrthophotos = []
      if (level.properties.hasFloorPlans?.length) {
        filteredOrthophotos = level.properties.hasFloorPlans
          .filter((floorPlan) => floorPlan.type === 'SCAN')
          .map((floorPlan) => convertFloorPlanToDrawing(floorPlan))
      }

      try {
        const entityService = useEntityService()
        const response = await entityService.getAll({
          page: 0,
          size: 1000,
          'labels.type': 'FLOOR_PLAN',
          'path.main.or': `*.${level.id}.*`
        })
        const entityDrawings = response.content.map(convertEntityToDrawing).filter(Boolean)
        filteredDrawings = [...filteredDrawings, ...entityDrawings.filter((d) => d.type !== 'SCAN')]
        filteredOrthophotos = [
          ...filteredOrthophotos,
          ...entityDrawings.filter((d) => d.type === 'SCAN')
        ]
      } catch (e) {
        console.debug('No drawing entities for this level:', e)
      }

      piniaStore().setFilteredDrawings(filteredDrawings)
      piniaStore().setFilteredOrthophotos(filteredOrthophotos)

      commit(SET_CURRENT_LEVEL, level)
      dispatch('planview/onLevelChange', level.id)
      await dispatch('GET_ROOMS_FOR_SELECTED_LEVEL')
    },
    // Takes a payload with real estate id: String
    async [CHANGE_REAL_ESTATE]({ commit, dispatch, state }, payload) {
      // useSelectionStore().set([payload.realEstate.id])

      commit(SET_SHOW_ZONE_MENU, false)
      piniaStore().setShowLineMenu(false)
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      commit(RESET_CURRENT_ROOM)
      dispatch('RESET_CURRENT_LEVEL')
      commit('mapview/' + RESET_CURRENT_BUILDING)
      if (!payload.realEstate) {
        dispatch('mapview/' + RESET_CURRENT_REAL_ESTATE)
        return
      }
      if (payload.realEstate.complexId && payload.realEstate.complexId !== 'undefined')
        dispatch('setSelectedComplexById', payload.realEstate.complexId)
      else commit('mapview/' + RESET_CURRENT_COMPLEX_LAYER)
      piniaStore().setFilteredDrawings([])
      piniaStore().setFilteredLines([])
      piniaStore().setFilteredOrthophotos([])
      piniaStore().setFilteredDrawings(
        payload.realEstate.outdoorPlans?.length
          ? payload.realEstate.outdoorPlans.map((outdoorPlan) =>
              convertOutdoorPlanToDrawing(outdoorPlan)
            )
          : []
      )
      piniaStore().setFilteredOrthophotos(
        payload.realEstate.orthophotos?.length
          ? payload.realEstate.orthophotos.map((outdoorImage, index) =>
              convertOutdoorImageToOrthophoto(outdoorImage, index)
            )
          : []
      )
      if (
        payload.realEstate.navvisLinks.length &&
        payload.realEstate.navvisLinks.some((x) => x.order)
      ) {
        payload.realEstate.navvisLinks.sort(orderByOrder)
      }
      const navvisLinksArray = []
      payload.realEstate.navvisLinks.forEach((el) => {
        const lastCharacter = el.link.charAt(el.link.length - 1)
        navvisLinksArray.push({
          link: lastCharacter === '/' ? el.link : el.link + '/',
          title: el.title
        })
      })
      payload.realEstate.navvisLinks = navvisLinksArray
      commit('mapview/' + SET_CURRENT_REAL_ESTATE, payload)
      commit(
        SET_CURRENT_DRONEVIEW_LINK_ID,
        state.mapview.selectedRealEstate?.droneviewLinks?.length ? '0' : null
      )
      commit('mapview/' + SET_ZONE_CATEGORIES_FOR_REAL_ESTATE, { categories: [] })
      piniaStore().setLineCategoriesForRealEstate([])
      // Keep filtered layers not related to outdoor zones
      let filteredLayers = state.mapview.filteredLayers.filter(
        (layer) => layer.id === 'building' || layer.id === 'realestates'
      )
      commit('mapview/' + SET_FILTERED_MAP_LAYERS, { filteredLayers })
      await dispatch('mapview/onRealEstateChange', payload.realEstate.id)
    },
    async [CHANGE_ROOM]({ commit, state }, payload) {
      // useSelectionStore().set([payload.roomId])
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      if (payload.roomId == null) return
      const room = state.roomsForSelectedLevel?.data.features.find(
        (room) => room.properties.id === payload.roomId
      )
      if (room == null) {
        commit(RESET_CURRENT_ROOM)
        return
      }
      const roomInfo = await getSpacev1(payload.roomId, true)
      if (roomInfo?.measurements?.length)
        room.properties.measurements.nta =
          roomInfo.measurements[0].NTA ?? roomInfo.measurements[0].Area
      commit(SET_CURRENT_ROOM, { ...room })
    },
    async [GET_ROOMS_FOR_SELECTED_LEVEL]({ commit, getters, state }) {
      try {
        const rooms = await getRoomsForSelectedLevelv1(
          state.mapview.selectedBuilding.id,
          getters.selectedLevelId
        )
        commit(SET_ROOMS_FOR_SELECTED_LEVEL, { rooms })
      } catch (e) {
        console.error(e)
        commit(SET_ROOMS_FOR_SELECTED_LEVEL, { rooms: null })
      }
    },
    async [RESET_ALL_SELECTIONS]({ commit, dispatch }) {
      // useSelectionStore().clear()
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      commit(RESET_CURRENT_ROOM)
      await dispatch('RESET_CURRENT_LEVEL')
      commit('mapview/' + RESET_CURRENT_BUILDING)
      await dispatch('mapview/' + RESET_CURRENT_REAL_ESTATE)
      commit('mapview/' + RESET_CURRENT_COMPLEX_LAYER)
    },
    [RESET_CURRENT_LEVEL]({ state }) {
      state.selectedLevel = null
    },
    [RESET_PLANVIEW_SELECTIONS]({ commit }) {
      piniaStore().setSelectedLine(null)
      piniaStore().setSelectedZone(null)
      commit(RESET_CURRENT_ROOM)
    },
    async setSelectedRealEstateById({ commit, state }, realEstateId) {
      if (state.mapview.selectedRealEstate && state.mapview.selectedRealEstate.id === realEstateId)
        return
      const realEstateToSelect = state.realEstateSource.data?.features.find(
        (realEstate) => realEstate.properties.id === realEstateId
      )
      if (realEstateToSelect) {
        commit('mapview/SET_CURRENT_REAL_ESTATE', { realEstate: realEstateToSelect.properties })
      }
    },
    setSelectedComplexById({ commit, state }, complexId) {
      if (state.mapview.selectedComplex && state.mapview.selectedComplex.id === complexId) return
      const complexToSelect = state.complexSource.data.features.find(
        (complex) => complex.properties.belongsToId === complexId
      )
      if (complexToSelect)
        commit('mapview/SET_CURRENT_COMPLEX_LAYER', { complex: complexToSelect.properties })
    },
    async [IMPORT_STATE]({ commit, getters, state, dispatch }, payload) {
      const { complex, realEstate, buildings, level } = payload

      function hasComplexChanged(complex) {
        const currentComplex = state.mapview.selectedComplex
        if (currentComplex && !complex) return true
        if (!currentComplex && complex) return true
        return currentComplex?.belongsToId !== complex?.belongsToId
      }

      function hasRealEstateChanged(realEstate) {
        const currentRealEstate = state.mapview.selectedRealEstate
        if (currentRealEstate && !realEstate) return true
        if (!currentRealEstate && realEstate) return true
        return currentRealEstate?.id !== realEstate?.id
      }

      function hasBuildingsChanged(buildings) {
        let currentBuildingIds = []
        if (state.mapview.selectedBuilding != {} && state.mapview.selectedBuilding.id) {
          currentBuildingIds = [state.mapview.selectedBuilding.id]
        }
        let buildingIds = []
        if (buildings) {
          buildingIds = buildings.map((x) => x.id)
        }
        return currentBuildingIds.sort().join(',') !== buildingIds.sort().join(',')
      }

      function hasLevelChanged(level) {
        return state.selectedLevel?.level !== level?.level
      }

      // Here we want to figure out at what granularity we should change the view
      // We need to know if the complex, realEstate, building or level has changed

      // If nothing has changed, do nothing
      if (
        !hasComplexChanged(complex) &&
        !hasRealEstateChanged(realEstate) &&
        !hasBuildingsChanged(buildings) &&
        !hasLevelChanged(level)
      ) {
        return
      }

      // Store variable to keep track of the current clicked item type
      let clickedItemType
      let currentView = 'mapview'

      // 1. Check if the complex has changed
      if (hasComplexChanged(complex)) {
        if (complex) {
          // 1.1 If the complex has changed to a valid complex, we should change the complex
          await dispatch('CHANGE_COMPLEX', { complex })
          clickedItemType = 'complex'
        } else {
          // 1.2 If the complex has changed to undefined, we should reset the complex
          await dispatch('RESET_ALL_SELECTIONS')
          clickedItemType = 'region'
        }
      }

      // 2. Check if the realEstate has changed
      if (hasRealEstateChanged(realEstate)) {
        if (realEstate) {
          // 2.1 If the realEstate has changed to a valid realEstate, we should change the realEstate
          await dispatch('CHANGE_REAL_ESTATE', { realEstate })
          clickedItemType = 'realEstate'
        } else {
          // 2.2 If the realEstate has changed to undefined, we should reset the realEstate
          clickedItemType = 'complex'
        }
      }

      // 3. Check if the buildings has changed
      if (hasBuildingsChanged(buildings)) {
        if (buildings?.length === 0) {
          // 3.1 If the buildings has changed to an empty array, we should reset the building
          await dispatch('CHANGE_BUILDING', { building: {} })
          clickedItemType = 'realEstate'
        }
        if (buildings?.length === 1) {
          // 3.2 If the buildings has changed to an array with one building, we should change the building
          const building = getters.allBuildings.find((x) => x.properties.id === buildings[0].id)
          await dispatch('CHANGE_BUILDING', { building: building ?? {} })
          clickedItemType = 'building'
        }
        if (buildings && buildings.length > 1) {
          // 3.3 If the buildings has changed to an array with multiple buildings, we should change the realEstate
          clickedItemType = 'building'
        }
      }

      // 4. Check if the level has changed
      if (hasLevelChanged(level)) {
        if (level) {
          // 4.1 If the level has changed to a valid level, we should change the level
          await dispatch('CHANGE_LEVEL', { levelNumber: level.level })
          clickedItemType = 'level'
        } else {
          // 4.2 If the level has changed to undefined, we should reset the level and planview selections
          await dispatch('RESET_CURRENT_LEVEL')
          await dispatch('RESET_PLANVIEW_SELECTIONS')
          clickedItemType = 'building'
        }
      }

      commit('SET_CLICKED_ITEM_TYPE', { type: clickedItemType })
      commit('SET_CURRENT_VIEW', { view: currentView })
    }
  },
  modules: {
    iv: indoorviewer,
    mapview,
    planview,
    dashboardview
  }
})
